import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormControl, FormRecord, ReactiveFormsModule } from '@angular/forms';
import {
  DropdownData,
  GenericDropdownComponent,
  CurrencyInputFieldComponent,
  CurrencyFormatPipe
} from '@zayan-erp/shared';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import { ProductAttributeGroup, ProductOption, SelectedProductForm } from '../../../../models/deal-options.models';
import { ModelElevationProductsService } from '../../../../services/model-elevation-products.service';
import { rxResourceFixed } from '@zayan-erp/shared';
import { map, of } from 'rxjs';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'app-selected-product-panel',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    GenericDropdownComponent,
    InputTextModule,
    TextareaModule,
    AccordionModule,
    NgSelectModule,
    CurrencyInputFieldComponent,
    CurrencyFormatPipe
  ],
  templateUrl: './selected-product-panel.component.html',
  styleUrls: ['./selected-product-panel.component.scss']
})
export class SelectedProductPanelComponent {
  readonly #modelElevationProductsService = inject(ModelElevationProductsService);
  readonly addendumId = input.required<string>();
  readonly product = input.required<ProductOption>();
  readonly selectionForm = input.required<SelectedProductForm>();
  readonly showDeleteIcon = input<boolean>(false);
  readonly submitAttempted = input<boolean>(false);
  readonly hasValidationErrors = input<boolean>(false);
  readonly validationErrorCount = input<number>(0);
  readonly removeRequested = output<void>();

  get attributeValuesGroup(): FormRecord<FormControl<string | null>> {
    return this.selectionForm().controls.attributeValues;
  }

  readonly productAttributesResource = rxResourceFixed({
    params: () => ({ productId: this.product().productId }),
    defaultValue: [] as ProductAttributeGroup[],
    stream: ({ params }) => {
      if (!params.productId) return of<ProductAttributeGroup[]>([]);
      return this.#modelElevationProductsService.getProductAttributes(params.productId);
    }
  });

  readonly productAttributes = computed(() => this.productAttributesResource.value() ?? []);

  readonly locationOptions = rxResourceFixed({
    params: () => ({ productId: this.product().productId, addendumId: this.addendumId() }),
    defaultValue: [] as DropdownData[],
    stream: ({ params }) => {
      if (!params.productId) return of<DropdownData[]>([]);
      return this.#modelElevationProductsService.getProductLocations(params.addendumId, params.productId).pipe(
        map(locations =>
          locations.map(location => ({
            id: location.id,
            firstLabel: location.locationCode,
            secondLabel: location.locationName
          }))
        )
      );
    }
  });
  readonly attributeValueOptions = computed(() => {
    const map = new Map<string, DropdownData[]>();
    this.productAttributes().forEach(attribute => {
      map.set(
        attribute.subCategoryAttributeId,
        attribute.productAttributeValues.map(value => ({
          id: value.id,
          firstLabel: value.value
        }))
      );
    });
    return map;
  });

  constructor() {
    effect(() => {
      const attributes = this.productAttributes();
      const formRecord = this.attributeValuesGroup;
      const controls = formRecord.controls;

      if (!attributes.length) return;

      const attributeIds = new Set(attributes.map(attribute => attribute.subCategoryAttributeId));

      attributes.forEach(attribute => {
        if (!controls[attribute.subCategoryAttributeId]) {
          const control = new FormControl<string | null>(null);
          if (this.submitAttempted()) {
            control.markAsTouched();
          }
          formRecord.addControl(attribute.subCategoryAttributeId, control);
        }
      });

      Object.keys(controls).forEach(controlName => {
        if (!attributeIds.has(controlName)) {
          formRecord.removeControl(controlName);
        }
      });
    });
  }

  remove(): void {
    this.removeRequested.emit();
  }
}
